/** Seller type for Stripe Connect */
export type SellerType = "individual" | "business";

/** Step 1 — Account type + basics (include fields Stripe often requires so we don't need hosted onboarding) */
export interface Step1Form {
  accountType: SellerType;
  country: string;
  email: string;
  /** Industry / category. Required for both individual and business. */
  industry: string;
  /** Business or seller website. Required for both individual and business. */
  businessWebsite: string;
}

/** Address used in step 2 */
export interface AddressForm {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
}

/** Step 2 — Individual (include phone for Stripe) */
export interface Step2IndividualForm {
  firstName: string;
  lastName: string;
  dob: string; // YYYY-MM-DD
  phone: string;
  address: AddressForm;
}

/** Step 2 — Business representative (include phone for Stripe) */
export interface RepresentativeForm {
  firstName: string;
  lastName: string;
  dob: string;
  phone: string;
  address: AddressForm;
}

/** Step 2 — Business (include business phone so Stripe doesn't ask on hosted page) */
export interface Step2BusinessForm {
  businessName: string;
  businessAddress: AddressForm;
  businessPhone: string;
  taxId: string;
  representative: RepresentativeForm;
}

export type Step2Form = Step2IndividualForm | Step2BusinessForm;

/** Step 3 — Payout: bank account + SSN last 4 (related to bank verification). Only token id sent to backend; never raw account numbers. */
export interface Step3Form {
  bankAccountConnected: boolean;
  accountHolderName?: string;
  accountHolderType?: "individual" | "company";
  /** Last 4 digits of SSN (US). Sent to backend with payout; do not persist. */
  ssn_last_4?: string;
  /** Sent to backend only at submit time (Stripe bank account token id, e.g. btok_xxx). Not stored. */
  externalAccountToken?: string;
}

/** Prefill from Stripe account (backend fetches via stripe.accounts.retrieve). Used when stripeAccountId exists; empty when no account or no data. */
export interface AccountDetailsPrefill {
  step1?: Partial<Step1Form>;
  step2Individual?: Partial<Step2IndividualForm>;
  step2Business?: Partial<Step2BusinessForm>;
  step3?: Pick<Step3Form, "bankAccountConnected">;
}

export type StripeAccountStatusCode =
  | "ACTIVE"
  | "UNDER_REVIEW"
  | "ACTION_REQUIRED"
  | "RESTRICTED"
  | "PENDING";

/** Stripe Connect account status from backend */
export interface StripeAccountStatus {
  stripeAccountId: string | null;
  /** Current onboarding step (1–5) saved in DB. Source of truth for which step to show. */
  onboardingStep: number | null;
  /** Account details from Stripe (when stripeAccountId exists). Used to prefill all steps. */
  accountDetails?: AccountDetailsPrefill | null;
  payoutsEnabled: boolean;
  /** Server-computed status derived from Stripe account fields. */
  status: StripeAccountStatusCode;
  requirements: {
    currentlyDue: string[];
    pastDue: string[];
    pendingVerification?: string[];
  };
  disabledReason: string | null;
}

/** Result of mapping Stripe requirements to onboarding step */
export interface OnboardingStepResult {
  stepNumber: number;
  messages: string[];
  status: "incomplete" | "pending" | "active" | "action_required";
}

/** Persisted progress (non-sensitive only) for "continue later" */
export interface SellerSetupProgress {
  step: number;
  step1: Step1Form;
  step2Individual?: Step2IndividualForm;
  step2Business?: Step2BusinessForm;
  step3?: Step3Form;
  lastSavedAt?: number;
}

export const SELLER_SETUP_STORAGE_KEY = "fiddlepiddle_seller_setup_progress";
export const TOTAL_STEPS = 5;
